import { NetworkStatus } from "@apollo/client";
import {
  useWsmProductCategoriesSetMutation,
  useWsmProductSecondaryCategoryFeedQuery,
  useWsmProductSecondaryCategoryNamesQuery,
} from "@dashboard/graphql";
import { Option } from "@saleor/macaw-ui-next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface UseSecondaryCategoriesResult {
  selectedSecondaryCategories: Option[];
  /**
   * Replaces the selection. `keepIds` names categories the picker could not
   * show (the form's current primary is filtered out of its value), so the
   * list it emits never contains them; any of those already selected are
   * kept rather than dropped by an edit to another chip.
   */
  setSelectedSecondaryCategories: (categories: Option[], keepIds?: string[]) => void;
  save: (currentPrimaryCategoryId: string | undefined) => Promise<void>;
  loading: boolean;
  /**
   * True only when the whole saved set has been read from the network: the
   * feed's network read finished (cached data under cache-and-network does
   * not count) with no further page, and the names query finished, both
   * without error. The write replaces the whole set server-side, so editing
   * or saving before this is true would silently drop rows never read.
   */
  ready: boolean;
  error: string | null;
  isDirty: boolean;
}

export const SECONDARY_CATEGORIES_READ_FAILED =
  "Secondary categories could not be loaded, so they cannot be saved. Reload the page and try again.";
export const SECONDARY_CATEGORIES_TOO_MANY =
  "This product has more than 100 secondary categories, so they cannot be edited here without losing rows.";
export const SECONDARY_CATEGORIES_SAVE_FAILED = "Failed to save secondary categories";

const sortedIds = (options: Option[]) => options.map(option => option.value).sort();

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id, i) => id === b[i]);

/**
 * makeMutation resolves a transport or GraphQL failure with `errors` set to
 * the ApolloError itself, not the GraphQLError array FetchResult declares;
 * read either shape.
 */
const transportErrorMessage = (errors: unknown): string | undefined => {
  if (Array.isArray(errors)) {
    return (errors[0] as { message?: string } | undefined)?.message || undefined;
  }

  return (errors as { message?: string } | null | undefined)?.message || undefined;
};

/**
 * The product's extra categories, read from wsmProductCategoryFeed and
 * written back with wsmProductCategoriesSet, a whole-set replace that never
 * carries the primary category (the server drops it if sent). The feed
 * carries ids only, so names come from the upstream categories query.
 */
export const useSecondaryCategories = (
  productId: string | undefined,
  primaryCategoryId: string | undefined,
): UseSecondaryCategoriesResult => {
  const [selectedSecondaryCategories, setSelectedSecondaryCategoriesState] = useState<Option[]>([]);
  const initialIdsRef = useRef<string[]>([]);
  // Mirrors the selection so the load effect can tell a dirty picker without
  // depending on it (it must run on a new id list, not on every edit).
  const selectionRef = useRef<Option[]>([]);

  selectionRef.current = selectedSecondaryCategories;

  const feed = useWsmProductSecondaryCategoryFeedQuery({
    variables: { ids: [productId ?? ""] },
    skip: !productId,
    fetchPolicy: "cache-and-network",
  });
  const categoryIds = useMemo(
    () =>
      (feed.data?.wsmProductCategoryFeed?.edges ?? [])
        .map(edge => edge.node)
        .filter(node => node.productId === productId && node.categoryId !== primaryCategoryId)
        .map(node => node.categoryId),
    [feed.data, productId, primaryCategoryId],
  );
  const names = useWsmProductSecondaryCategoryNamesQuery({
    variables: { ids: categoryIds },
    skip: categoryIds.length === 0,
  });
  const loadedKey = categoryIds.join(",");
  const namesReady = categoryIds.length === 0 || !!names.data;
  const loading = (feed.loading && !feed.data) || (names.loading && !names.data);
  const hasMore = feed.data?.wsmProductCategoryFeed?.pageInfo?.hasNextPage === true;
  const readError = feed.error ?? names.error;
  // cache-and-network hands back cached data while the network read is still
  // in flight; a save from that stale set would overwrite rows added since.
  const feedFromNetwork = feed.networkStatus === NetworkStatus.ready && !feed.loading;
  const ready = !!feed.data && feedFromNetwork && namesReady && !readError && !hasMore;

  useEffect(() => {
    if (!productId || !feed.data || !namesReady) {
      return;
    }

    const byId = new Map(
      (names.data?.categories?.edges ?? []).map(edge => [edge.node.id, edge.node] as const),
    );
    const options = categoryIds.map(id => {
      const category = byId.get(id);

      if (!category) {
        return { label: id, value: id };
      }

      return {
        label: category.parent ? `${category.parent.name} / ${category.name}` : category.name,
        value: id,
      };
    });
    const dirty = !sameIds(sortedIds(selectionRef.current), initialIdsRef.current);

    // A new saved set (a refetch, or a product save that moved the primary)
    // must not throw away edits the merchant has not saved yet: keep the
    // selection and move only the baseline it is compared against.
    if (!dirty) {
      setSelectedSecondaryCategoriesState(options);
    }

    initialIdsRef.current = sortedIds(options);
    // loadedKey is the id list; names.data arrives once per list.
  }, [productId, loadedKey, namesReady, names.data]);

  // Read failures only. A failed save is reported by the caller from the
  // error save() throws, so it is never toasted twice.
  const error = readError
    ? `${SECONDARY_CATEGORIES_READ_FAILED} (${readError.message})`
    : hasMore
      ? SECONDARY_CATEGORIES_TOO_MANY
      : null;

  const setSelectedSecondaryCategories = useCallback((categories: Option[], keepIds?: string[]) => {
    setSelectedSecondaryCategoriesState(previous => {
      if (!keepIds?.length) {
        return categories;
      }

      const emitted = new Set(categories.map(option => option.value));
      const kept = previous.filter(
        option => keepIds.includes(option.value) && !emitted.has(option.value),
      );

      return kept.length ? [...categories, ...kept] : categories;
    });
  }, []);

  const isDirty = !sameIds(sortedIds(selectedSecondaryCategories), initialIdsRef.current);

  // save() throws what goes wrong and the page toasts it once; the payload
  // errors must not also be toasted by makeMutation.
  const [setCategories] = useWsmProductCategoriesSetMutation({
    disableNestedErrorHandling: true,
  });
  /**
   * Writes the selection. `currentPrimaryCategoryId` is the primary the
   * product was just saved with (the form's value, not the loaded one), so a
   * category promoted to primary in the same save is not also sent as
   * secondary. Throws, without writing, when the saved set was not fully
   * read; the caller reports the thrown message.
   */
  const save = useCallback(
    async (currentPrimaryCategoryId: string | undefined) => {
      if (!productId) {
        return;
      }

      const ids = selectedSecondaryCategories
        .map(option => option.value)
        .filter(id => id !== currentPrimaryCategoryId);
      // A category just promoted to primary still sits in the saved set until
      // the set is rewritten without it.
      const holdsNewPrimary = ids.length !== selectedSecondaryCategories.length;

      if (!isDirty && !holdsNewPrimary) {
        return;
      }

      // Nothing to write returns above, so a product save is never failed by
      // a set the user did not touch; anything else is refused unwritten.
      if (!ready) {
        throw new Error(hasMore ? SECONDARY_CATEGORIES_TOO_MANY : SECONDARY_CATEGORIES_READ_FAILED);
      }

      const result = await setCategories({
        variables: {
          product: productId,
          categories: ids,
        },
      });
      const payload = result?.data?.wsmProductCategoriesSet;

      // No payload is a transport or GraphQL failure, not a clean write: the
      // generated hook resolves those instead of rejecting.
      if (!payload) {
        throw new Error(transportErrorMessage(result?.errors) ?? SECONDARY_CATEGORIES_SAVE_FAILED);
      }

      const errors = payload.errors ?? [];

      if (errors.length > 0) {
        throw new Error(errors.map(err => err.message || err.code).join("; "));
      }

      // The baseline is what was written (the primary filtered out), so the
      // picker is clean against it even before the refetch lands.
      initialIdsRef.current = [...ids].sort();

      const written = new Set(ids);

      // A primary dropped from the write leaves the selection too, or it
      // would read dirty against the written baseline from here on.
      if (holdsNewPrimary) {
        setSelectedSecondaryCategoriesState(previous =>
          previous.filter(option => written.has(option.value)),
        );
      }

      // The write went through; a failed re-read must not report the save as
      // failed. The feed's own error surfaces as a read failure instead.
      try {
        await feed.refetch();
      } catch (err) {
        console.error(err);
      }
    },
    [productId, ready, hasMore, isDirty, selectedSecondaryCategories, setCategories, feed],
  );

  return {
    selectedSecondaryCategories,
    setSelectedSecondaryCategories,
    save,
    loading,
    ready,
    error,
    isDirty,
  };
};
